// TODO: Наладить типизацию

type TargetType<T> = T | T[] | any
type TargetLambda<T> = (() => TargetType<T>)
type TargetLambdaOrType<T> = TargetLambda<T> | TargetType<T>
type ConditionLambda<T> = ((target: TargetType<T>) => boolean)
type Condition<T> = ConditionLambda<T> | TargetType<T>
type Effect<T, R> = (target: TargetType<T>) => R
type WhenFn<T, R, RE = IOnFunction<T, R> | DefaultOnFunction<T, R>> = RE[]
type WhenTarget<T> = TargetLambdaOrType<T>


interface IOnFunction<T, R> {
  call(target: TargetType<T>): R
  checkCondition(target: TargetType<T>): boolean
}


class OnFunction<T, R> implements IOnFunction<T, R> {

  constructor(private readonly condition: Condition<T>, private readonly effect: Effect<T, R>) {
  }

  public checkCondition(target: TargetType<T>): boolean {
    return calculateCondition(target, this.condition)
  }

  public call(target: TargetType<T>): R {
    return this.effect(target)
  }
}


class DefaultOnFunction<T, R> implements IOnFunction<T, R> {

  constructor(private readonly effect: Effect<T, R>) {
  }

  public call(target: TargetType<T>): R {
    return this.effect(target)
  }

  public checkCondition(target: TargetType<T>): boolean {
    return true
  }
}


class When<T, R> {
  private defaultOn: DefaultOnFunction<T, R>
  private readonly targetResult = calculateTarget(this.target)

  constructor(private readonly target: TargetLambdaOrType<T>, private readonly onFunctions: IOnFunction<T, R>[]) {
  }

  public calculate(): R {
    return this.callLoop()
  }

  private callLoop(): any {
    for (let i = 0; i < this.onFunctions.length; i++) {
      const fun = this.onFunctions[i]
      if (this.checkDeferredCall(fun)) continue
      if (fun.checkCondition(this.targetResult)) return fun.call(this.targetResult)
    }
    if (this.defaultOn) return this.defaultOn.call(this.targetResult)
  }

  private checkDeferredCall(fun: IOnFunction<T, R>) {
    if (fun instanceof DefaultOnFunction) {
      if (this.defaultOn) throw new Error('default is init')
      this.defaultOn = fun
      return true
    }
    return false
  }
}


/**
 * Аналог when из Kotlin
 * @param target - проверяемое значение
 * @param args - множество проверочных вырожений
 * @return функция вернет результат работы эффекта первого найденного истинного проверочного выражения
 *  или при отсутствие совподений вернется результат дефолтного эффекта при его наличии
 *
 * ## Как это работает?
 * ## оч просто
 * ### Если target не коллекция и condition не коллекция то значения просто сравниваются
 * ### Example
 * ```ts
 * ```
 *
 * если target не коллекция и condition коллекция то если
 */
export function when<T, R>(target: WhenTarget<T>, ...args: WhenFn<T, R>): R {
  const whenInstance = new When<T, R>(target, args)
  return whenInstance.calculate()
}

/**
 * Фабрика проверочного выражения
 * @param condition - условие совпадения
 *  при использование условия как картежа условие считается истинным при совпадение с одним из элементов
 * @param effect - колбек выполняющийся при истинновти проверочного выражения
 * @return проверочное выражение возвращает результат работы эффекты
 */
export function on<T, R>(condition: Condition<T>, effect: Effect<T, R>): OnFunction<T, R> {
  return new OnFunction<T, R>(condition, effect)
}

/**
 * Фабрика выражения по умолчанию
 * выполнится при не совподение всех проверочных выражений
 * в множестве может содержаться только одно выражение по умолчанию
 * @param effect - колбек выполняющийся при отсутствие совпабений
 * @return вернет результат работы эффекта
 */
export function defaultOn<T, R>(effect: Effect<T, R>): DefaultOnFunction<T, R> {
  return new DefaultOnFunction(effect)
}

function calculateTarget<T>(target: TargetLambdaOrType<T>): TargetType<T> {
  if (typeof target === 'function') {
    return (target as TargetType<T>)()
  }
  return target
}

function calculateCondition<T>(target: TargetType<T>, condition: Condition<T>): boolean {
  if (typeof condition === 'function') return calculateFunctionCondition(target, condition as ConditionLambda<T>)
  if (Array.isArray(target) && Array.isArray(condition)) return calculateMergeCondition(target, condition as any)
  if (!Array.isArray(target) && Array.isArray(condition)) return calculateArrayCondition(target, condition as any)
  if (Array.isArray(target) && !Array.isArray(condition)) return calculateTargetArrayCondition(target, condition)
  return target === condition
}

function calculateFunctionCondition<T>(target: T, condition: ConditionLambda<T>): boolean {
  return condition(target)
}

function calculateArrayCondition<T>(target: T, condition: T[]): boolean {
  return condition.some(c => calculateCondition(target, c))
}

function calculateMergeCondition<T>(target: T[], condition: T[]): boolean {
  if (target.length !== condition.length) return false
  for (let i = 0; i < target.length; i++) {
    if (!calculateCondition(target[i], condition[i])) return false
  }
  return true
}

function calculateTargetArrayCondition<T>(target: T[], condition: T): boolean {
  return target.some(t => calculateCondition(t, condition))
}
